import moment from "moment";
import React, { useRef, useState } from "react";
import { ReactComponent as Arrow } from "../../assets/img/Arrowdown.svg";
import { ReactComponent as PackageIcon } from "../../assets/img/addIcon.svg";
import { getMediaUrl } from "config";
import api from "api";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import EditBatch from "views/BrandOrder/EditBatch";

// --- Code type badge helpers ---
const codeTypeBadge = (codeType) => {
    const type = (codeType || '').toLowerCase();
    let label, bgColor, textColor, icon;

    if (type === 'nfc') {
        label = 'NFC';
        bgColor = 'rgba(54,47,216,0.1)';
        textColor = '#362FD8';
        icon = (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#362FD8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36" />
                <path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58" />
                <path d="M12.91 4.1a16.1 16.1 0 0 1 0 15.8" />
                <path d="M16.37 2a20.16 20.16 0 0 1 0 20" />
            </svg>
        );
    } else if (type === 'codeless') {
        label = 'Codeless';
        bgColor = 'rgba(255,152,0,0.1)';
        textColor = '#E65100';
        icon = (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
        );
    } else {
        // regular / default
        label = 'Regular';
        bgColor = 'rgba(0,178,7,0.1)';
        textColor = '#00B207';
        icon = (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00B207" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
            </svg>
        );
    }

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: bgColor,
                color: textColor,
                whiteSpace: 'nowrap',
            }}
        >
            {icon}
            {label}
        </span>
    );
};

// --- Batch image upload zone ---
const BatchImageUpload = ({ batch, onImageUploaded }) => {
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [localPreview, setLocalPreview] = useState(null);

    const handleFileSelect = (selectedFile) => {
        if (!selectedFile) return;
        if (selectedFile.size > 5 * 1024 * 1024) {
            return toast.error("Image must be under 5MB");
        }
        if (!selectedFile.type.startsWith("image/")) {
            return toast.error("File must be an image");
        }
        setLocalPreview(URL.createObjectURL(selectedFile));
        uploadImage(selectedFile);
    };

    const uploadImage = (file) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('product_img', file);
        api('put', `/requests/${batch._id}`, formData)
            .then((res) => {
                setUploading(false);
                toast.success("Image updated");
                if (onImageUploaded) onImageUploaded();
            })
            .catch((err) => {
                setUploading(false);
                toast.error(err?.response?.data?.message || "Failed to update image");
            });
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const imagePreview = localPreview
        ? localPreview
        : batch?.product_img
            ? getMediaUrl(batch.product_img)
            : null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: dragging ? '#EEF0FF' : '#f5f5f5',
                    border: dragging ? '2px dashed #362FD8' : '2px dashed transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    position: 'relative',
                }}
                title="Click or drag to upload product image"
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={(e) => {
                        if (e.target.files.length) handleFileSelect(e.target.files[0]);
                    }}
                />
                {uploading && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 2,
                    }}>
                        <div style={{
                            width: 20, height: 20,
                            border: '2px solid #362FD8',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                        }} />
                    </div>
                )}
                {imagePreview ? (
                    <img
                        src={imagePreview}
                        alt={batch.product_name || 'Product'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                    </svg>
                )}
            </div>
            <div style={{ fontSize: '10px', color: '#aaa', marginTop: '3px', textAlign: 'center', lineHeight: 1.2 }}>
                Rec: 400x400, max 5MB
            </div>
        </div>
    );
};

// --- Map icon SVG ---
const MapIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#362FD8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

// --- Editable fields for product name, description, COA link (compact horizontal) ---
const BatchEditableFields = ({ batch, codeType, unitCount, onSaved }) => {
    const [name, setName] = useState(batch.product_name || '');
    const [description, setDescription] = useState(batch.text || '');
    const [coaLink, setCoaLink] = useState(batch.coaLink || '');
    const [saving, setSaving] = useState(false);
    const [coaError, setCoaError] = useState(false);
    const timeoutRef = useRef(null);

    const isValidUrl = (str) => {
        if (!str) return true;
        try { new URL(str); return true; } catch { return false; }
    };

    const autoSave = (field, value) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (field === 'coaLink' && value && !isValidUrl(value)) {
            return;
        }
        timeoutRef.current = setTimeout(() => {
            setSaving(true);
            const body = {};
            body[field] = value;
            api('put', `/requests/${batch._id}`, body)
                .then(() => {
                    setSaving(false);
                    toast.success("Saved", { autoClose: 1500, hideProgressBar: true });
                    if (onSaved) onSaved();
                })
                .catch(() => {
                    setSaving(false);
                    toast.error("Failed to save");
                });
        }, 1000);
    };

    const inputStyle = {
        border: 'none',
        borderBottom: '1px solid transparent',
        outline: 'none',
        fontFamily: 'Outfit',
        backgroundColor: 'transparent',
        width: '100%',
        padding: '2px 4px',
        borderRadius: '2px',
        transition: 'all 0.2s',
    };

    const handleInputHover = (e, enter) => {
        if (document.activeElement !== e.target) {
            e.target.style.backgroundColor = enter ? '#F8F8F8' : 'transparent';
        }
    };

    const handleInputFocus = (e) => {
        e.target.style.borderBottomColor = '#362FD8';
        e.target.style.backgroundColor = '#fff';
    };

    const handleInputBlur = (e) => {
        e.target.style.borderBottomColor = 'transparent';
        e.target.style.backgroundColor = 'transparent';
    };

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            {/* Row 1: Product name + badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                    type="text"
                    value={name}
                    placeholder="Product name"
                    title="Displayed on the verification page when customers scan this product"
                    maxLength={50}
                    onChange={(e) => { setName(e.target.value); autoSave('product_name', e.target.value); }}
                    onMouseEnter={(e) => handleInputHover(e, true)}
                    onMouseLeave={(e) => handleInputHover(e, false)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={{ ...inputStyle, fontWeight: 600, fontSize: '14px', color: '#1C1C1C', flex: 1 }}
                />
                <span style={{ fontSize: '10px', color: name.length > 40 ? '#dc3545' : '#ccc', whiteSpace: 'nowrap' }}>{name.length}/50</span>
                {codeTypeBadge(codeType)}
                {unitCount != null && (
                    <span style={{ fontSize: '12px', color: '#555', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {unitCount.toLocaleString()} units
                    </span>
                )}
                {saving && (
                    <span style={{ fontSize: '11px', color: '#362FD8', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        ● Saving...
                    </span>
                )}
            </div>
            {/* Row 2: Description */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                    type="text"
                    value={description}
                    placeholder="Short description"
                    title="Short product description shown below the product name on the scan page"
                    maxLength={120}
                    onChange={(e) => { setDescription(e.target.value); autoSave('text', e.target.value); }}
                    onMouseEnter={(e) => handleInputHover(e, true)}
                    onMouseLeave={(e) => handleInputHover(e, false)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={{ ...inputStyle, fontSize: '13px', color: '#777', flex: 1 }}
                />
                <span style={{ fontSize: '10px', color: description.length > 100 ? '#dc3545' : '#ccc', whiteSpace: 'nowrap' }}>{description.length}/120</span>
            </div>
            {/* Row 3: COA link */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ color: '#999', fontSize: '12px', flexShrink: 0 }}>COA:</span>
                <input
                    type="url"
                    value={coaLink}
                    placeholder="https://example.com/coa.pdf"
                    title="Certificate of Analysis URL — shown as a clickable link on the verification page"
                    maxLength={250}
                    onChange={(e) => {
                        setCoaLink(e.target.value);
                        setCoaError(false);
                        autoSave('coaLink', e.target.value);
                    }}
                    onMouseEnter={(e) => handleInputHover(e, true)}
                    onMouseLeave={(e) => handleInputHover(e, false)}
                    onFocus={handleInputFocus}
                    onBlur={(e) => {
                        handleInputBlur(e);
                        if (e.target.value && !isValidUrl(e.target.value)) {
                            setCoaError(true);
                            e.target.style.borderBottomColor = '#dc3545';
                            toast.error("COA link must be a valid URL (e.g. https://example.com/coa.pdf)");
                        }
                    }}
                    style={{
                        ...inputStyle,
                        fontSize: '12px',
                        color: coaError ? '#dc3545' : '#362FD8',
                        borderBottomColor: coaError ? '#dc3545' : 'transparent',
                        flex: 1,
                    }}
                />
                <span style={{ fontSize: '10px', color: coaLink.length > 200 ? '#dc3545' : '#ccc', whiteSpace: 'nowrap' }}>{coaLink.length}/250</span>
                {coaLink && isValidUrl(coaLink) && (
                    <a
                        href={coaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '13px', color: '#362FD8', flexShrink: 0, textDecoration: 'none' }}
                        title="Open COA link"
                    >
                        ↗
                    </a>
                )}
            </div>
        </div>
    );
};

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);

const DetailTable = ({ arr, index, item, scanStats, getOrders }) => {
    const pendingBatch = item.batchIds.find(batch => batch.status === "pending");
    const [openDetail, setOpenDetail] = useState(false);
    const [editingBatch, setEditingBatch] = useState(null);

    const orderNumber = item?._id ? item._id.slice(-6).toUpperCase() : (index + 1);
    const batchCount = item.batchIds?.length || 0;
    const totalScans = item?.totalScansOfOrder || 0;
    const isPending = item.batchIds.length === 0 || pendingBatch;
    const codeType = item?.code_type || item?.batchIds?.[0]?.code_type || 'regular';

    const topState = scanStats?.topState || '-';
    const topCountry = scanStats?.topCountry || '-';
    const hasLocation = topState !== '-' || topCountry !== '-';

    const thStyle = {
        borderTop: 'none',
        background: 'white',
        fontSize: '14px',
        fontWeight: 400,
        color: '#1C1C1C',
        fontFamily: 'Outfit',
    };

    // Determine colSpan for expanded detail (must match column count)
    const colSpan = 9;

    return (
        <>
            <tr
                style={{
                    fontFamily: "Outfit",
                    color: "#1C1C1C",
                }}
                className="bg-white"
            >
                {/* Expand arrow */}
                <th style={{ borderBottomLeftRadius: arr[arr.length - 1] === index ? "10px" : "0px" }}>
                    <div
                        style={{
                            cursor: "pointer",
                            transition: "0.3s",
                            rotate: openDetail ? "none" : "-90deg",
                            textAlign: "center"
                        }}
                        onClick={() => setOpenDetail(!openDetail)}
                    >
                        <Arrow />
                    </div>
                </th>

                {/* Order # */}
                <td style={thStyle}>
                    <span className="mb-0 text-sm" style={{ fontWeight: 500 }}>
                        #{orderNumber}
                    </span>
                </td>

                {/* Products count */}
                <td style={thStyle}>
                    {batchCount} {batchCount === 1 ? 'product' : 'products'}
                </td>

                {/* Code Type */}
                <td style={{ ...thStyle, textAlign: 'center' }}>
                    {codeTypeBadge(codeType)}
                </td>

                {/* Status */}
                <td style={{ ...thStyle, textAlign: 'center' }}>
                    <div
                        className="rounded-pill d-flex justify-content-center align-items-center px-3 py-1"
                        style={{
                            backgroundColor: isPending ? "rgba(255, 0, 0, 0.1)" : "rgba(0, 178, 7, 0.1)",
                            display: 'inline-flex',
                        }}
                    >
                        <span
                            style={{
                                color: isPending ? "red" : "#00B207",
                                fontSize: "12px",
                                fontWeight: 400,
                            }}
                        >
                            {isPending ? "Pending" : "Approved"}
                        </span>
                    </div>
                </td>

                {/* Total Scans */}
                <td style={{ ...thStyle, textAlign: 'center' }}>
                    {totalScans}
                </td>

                {/* Top Location */}
                <td style={{ ...thStyle, textAlign: 'center' }}>
                    {hasLocation ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            {topState !== '-' && (
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#362FD8',
                                }}>
                                    {topState}
                                </span>
                            )}
                            {topCountry !== '-' && (
                                <span style={{
                                    fontSize: '11px',
                                    color: '#888',
                                }}>
                                    {topCountry}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span style={{ color: '#aaa' }}>-</span>
                    )}
                </td>

                {/* Date */}
                <td style={thStyle}>
                    {moment(item.createdAt).format("MMM DD, YYYY")}
                </td>

                {/* Map link */}
                <td style={{ ...thStyle, textAlign: 'center', borderBottomRightRadius: arr[arr.length - 1] === index ? "10px" : "0px" }}>
                    <Link
                        to={`/brand/map?orderId=${item._id}`}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(54,47,216,0.08)',
                            textDecoration: 'none',
                        }}
                        title="View on Activity Map"
                    >
                        <MapIcon />
                    </Link>
                </td>
            </tr>

            {/* Expanded batch details */}
            {openDetail && (
                <tr style={{ backgroundColor: "#F9F9FB", fontFamily: "Outfit" }}>
                    <td colSpan={colSpan} style={{ padding: '16px 24px' }}>
                        {item.batchIds.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#888', fontSize: '14px', padding: '12px 0' }}>
                                No products in this order yet.
                            </div>
                        ) : (
                            item.batchIds.map((batch, bIdx) => {
                                const batchScans = batch?.totalBatchScans || 0;
                                const batchIsPending = batch.status === "pending";
                                const unitCount = batch.number_of_codes;
                                return (
                                    <div
                                        key={batch._id || bIdx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px',
                                            padding: '12px',
                                            backgroundColor: '#fff',
                                            borderRadius: '8px',
                                            marginBottom: bIdx < item.batchIds.length - 1 ? '6px' : '0',
                                            border: '1px solid #F0F0F0',
                                        }}
                                    >
                                        {/* Product image with drag-and-drop upload */}
                                        <BatchImageUpload batch={batch} onImageUploaded={getOrders} />

                                        {/* Editable fields */}
                                        <BatchEditableFields batch={batch} codeType={codeType} unitCount={unitCount} onSaved={getOrders} />

                                        {/* Right side: status + scans + map */}
                                        <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', minWidth: '80px' }}>
                                            <div
                                                className="rounded-pill"
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '3px 10px',
                                                    backgroundColor: batchIsPending ? "rgba(255, 0, 0, 0.1)" : "rgba(0, 178, 7, 0.1)",
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    color: batchIsPending ? "red" : "#00B207",
                                                }}
                                            >
                                                {batch.status ? batch.status.charAt(0).toUpperCase() + batch.status.slice(1) : 'Pending'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#888' }}>
                                                {batchScans} {batchScans === 1 ? 'scan' : 'scans'}
                                            </div>
                                            <span
                                                onClick={() => setEditingBatch(batch)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontSize: '12px',
                                                    color: '#362FD8',
                                                    cursor: 'pointer',
                                                }}
                                                title="Edit batch"
                                            >
                                                <EditIcon />
                                                <span>Edit</span>
                                            </span>
                                            <Link
                                                to={`/brand/map?orderId=${item._id}&batchId=${batch._id}`}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontSize: '12px',
                                                    color: '#362FD8',
                                                    textDecoration: 'none',
                                                }}
                                                title="View on Activity Map"
                                            >
                                                <MapIcon />
                                                <span>Map</span>
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </td>
                </tr>
            )}

            {/* Inline keyframe for upload spinner */}
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
            {editingBatch && <EditBatch selectedBatch={editingBatch} handleModal={() => setEditingBatch(null)} getOrders={getOrders} />}
        </>
    );
};

export default DetailTable;
